import React, { useState } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Container,
  Box,
  Button,
  Card,
  CardContent,
  Step,
  Stepper,
  StepLabel,
  Typography,
  Alert,
  CircularProgress,
  Backdrop,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from '@mui/material';
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import QRCode from 'qrcode';

import UserInfoForm from './UserInfoForm';
import OtpInitiationStep from './OtpInitiationStep';
import OtpVerificationStep from './OtpVerificationStep';

const pulseKeyframes = {
  '0%': { transform: 'scale(1)', opacity: 1 },
  '50%': { transform: 'scale(1.15)', opacity: 0.7 },
  '100%': { transform: 'scale(1)', opacity: 1 },
};

const scanLineKeyframes = {
  '0%': { top: '10%' },
  '50%': { top: '85%' },
  '100%': { top: '10%' },
};

const dotKeyframes = {
  '0%, 20%': { opacity: 0 },
  '40%': { opacity: 1 },
  '100%': { opacity: 0 },
};

const useStyles = makeStyles({
  '@keyframes pulse': pulseKeyframes,
  '@keyframes scanLine': scanLineKeyframes,
  '@keyframes dotFade': dotKeyframes,
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    paddingTop: '2rem',
    paddingBottom: '2rem',
    backgroundColor: '#f5f5f5',
  },
  container: {
    maxWidth: '600px',
    width: '100%',
  },
  pollingCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
    padding: '2.5rem 2rem',
  },
  scannerBox: {
    position: 'relative',
    width: 120,
    height: 120,
    borderRadius: 16,
    background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(25, 118, 210, 0.15)',
  },
  scannerIcon: {
    fontSize: '3rem !important',
    color: '#1976d2',
    animation: '$pulse 2s ease-in-out infinite',
    zIndex: 1,
  },
  scanLine: {
    position: 'absolute',
    left: '10%',
    width: '80%',
    height: 3,
    borderRadius: 2,
    background: 'linear-gradient(90deg, transparent, #1976d2, transparent)',
    animation: '$scanLine 2.4s ease-in-out infinite',
  },
  dotContainer: {
    display: 'flex',
    gap: 6,
    justifyContent: 'center',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#1976d2',
    animation: '$dotFade 1.4s ease-in-out infinite',
  },
  userInfoBanner: {
    backgroundColor: '#e3f2fd',
    padding: '1rem 1.5rem',
    borderRadius: '8px',
    marginBottom: '1.5rem',
    border: '1px solid #90caf9',
  },
  card: {
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    marginBottom: '2rem',
  },
  stepperContainer: {
    padding: '2rem 1rem',
    backgroundColor: '#fff',
  },
  contentCard: {
    backgroundColor: '#fff',
    padding: '2rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  formField: {
    width: '100%',
  },
  buttonContainer: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-end',
    marginTop: '1.5rem',
  },
  stepContent: {
    marginTop: '2rem',
  },
  skipOtpContainer: {
    marginTop: '2rem',
    padding: '1rem',
    backgroundColor: '#fff3e0',
    borderRadius: '8px',
    textAlign: 'center',
  },
  dbicContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
  },
  methodButton: {
    minWidth: '150px',
  },
  buttonGroup: {
    display: 'flex',
    gap: '1rem',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
});

const STEPS = [
  { label: 'User Info', id: 'user-info' },
  { label: 'Phone possession', id: 'phone-possession' },
  { label: 'PBIC Verification', id: 'pbic-verification' },
  { label: 'FlexID Verification', id: 'flexid-verification' },
  { label: 'DBIC Verify', id: 'dbic-verification' },
  { label: 'Verified ID', id: 'verified-id' },
  { label: 'Complete', id: 'verification-complete' },
];

const TMX_PROFILES = {
  velocity: { chipLabel: 'Velocity', profileLabel: 'hsid_velocity' },
  phoneFinder: { chipLabel: 'Phone Finder', profileLabel: 'phone_finder_hsid' },
  flexid: { chipLabel: 'FlexID', profileLabel: 'flexid' },
  idverse: { chipLabel: 'IDVerse', profileLabel: 'Get IDVerse Results' },
};

const getStatusChipStyle = (status) => {
  const isPass = status?.toLowerCase() === 'pass';
  return {
    backgroundColor: isPass ? '#e8f5e9' : '#fdecea',
    color: isPass ? '#2e7d32' : '#c62828',
    fontWeight: 600,
    border: `1px solid ${isPass ? '#a5d6a7' : '#ef9a9a'}`,
  };
};

const OTPProgressPage = ({ threatMetrixScriptUrl }) => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [userInfo, setUserInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dob: '',
    nationalIdNumber: '',
    nationalIdType: '',
  });
  const [parsedPhone, setParsedPhone] = useState({ countryCode: '+1', telephone: '' });
  const [otpDeliveryMethod, setOtpDeliveryMethod] = useState('sms');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpChallengeId, setOtpChallengeId] = useState(null);

  const [stepStatus, setStepStatus] = useState({
    'user-info': 'pending',
    'phone-possession': 'pending',
    'pbic-verification': 'pending',
    'flexid-verification': 'pending',
    'dbic-verification': 'pending',
    'verified-id': 'pending',
    'verification-complete': 'pending',
  });

  const [tmxVelocityResponse, setTmxVelocityResponse] = useState(null);
  const [tmxVelocityReviewStatus, setTmxVelocityReviewStatus] = useState(null);
  const [tmxPhoneFinderResponse, setTmxPhoneFinderResponse] = useState(null);
  const [tmxPhoneFinderReviewStatus, setTmxPhoneFinderReviewStatus] = useState(null);
  const [tmxFlexIdResponse, setTmxFlexIdResponse] = useState(null);
  const [tmxFlexIdReviewStatus, setTmxFlexIdReviewStatus] = useState(null);
  const [tmxModalOpen, setTmxModalOpen] = useState(false);
  const [tmxModalProfile, setTmxModalProfile] = useState('velocity');

  const [dbicVerified, setDbicVerified] = useState(false);
  const [dbicPolling, setDbicPolling] = useState(false);
  const [dbicAuthSelectionID, setDbicAuthSelectionID] = useState(null);
  const [swalAlert, setSwalAlert] = useState(null);

  const [verifiedIdStatus, setVerifiedIdStatus] = useState(null);
  const [verifiedIdDetails, setVerifiedIdDetails] = useState(null);
  const [verifiedIdInProgress, setVerifiedIdInProgress] = useState(false);
  const [verifiedIdPreviousStatus, setVerifiedIdPreviousStatus] = useState(null);
  const [verifiedIdDialogOpen, setVerifiedIdDialogOpen] = useState(false);
  const [verifiedIdDialogTitle, setVerifiedIdDialogTitle] = useState('Verified ID Details');

  const [tmxIdverseResponse, setTmxIdverseResponse] = useState(null);
  const [tmxIdverseReviewStatus, setTmxIdverseReviewStatus] = useState(null);

  const [networkLogs, setNetworkLogs] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [responseModalOpen, setResponseModalOpen] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [responseViewMode, setResponseViewMode] = useState('json'); // 'json' or 'raw'

  const tmxResults = {
    velocity: { status: tmxVelocityReviewStatus, response: tmxVelocityResponse },
    phoneFinder: { status: tmxPhoneFinderReviewStatus, response: tmxPhoneFinderResponse },
    flexid: { status: tmxFlexIdReviewStatus, response: tmxFlexIdResponse },
    idverse: { status: tmxIdverseReviewStatus, response: tmxIdverseResponse },
  };

  const loggedFetch = async (url, options = {}, label) => {
    const start = Date.now();
    let profile = undefined;

    if (options.body && typeof options.body === 'string') {
      try {
        const parsedBody = JSON.parse(options.body);
        if (url.includes('/api/tmx/profile-query') && parsedBody.policy) {
          profile = parsedBody.policy;
        }
      } catch (e) {
        // ignore JSON parse errors for logging
      }
    }
    const entryBase = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      timestamp: new Date().toISOString(),
      label,
      method: options.method || 'GET',
      url,
      requestBody: options.body,
      profile,
    };

    try {
      const response = await fetch(url, options);
      const durationMs = Date.now() - start;

      let responseBody = '';
      let responseBodySnippet = '';
      try {
        const cloned = response.clone();
        const text = await cloned.text();
        responseBody = text;
        responseBodySnippet = text.slice(0, 500);
      } catch (e) {
        responseBody = '[unavailable]';
        responseBodySnippet = '[unavailable]';
      }

      setNetworkLogs((prev) => [
        ...prev,
        {
          ...entryBase,
          status: response.status,
          ok: response.ok,
          durationMs,
          responseBodySnippet,
          responseBody,
        },
      ]);

      return response;
    } catch (error) {
      const durationMs = Date.now() - start;
      setNetworkLogs((prev) => [
        ...prev,
        {
          ...entryBase,
          status: 'ERROR',
          ok: false,
          durationMs,
          error: error.message,
          responseBody: error.message,
          responseBodySnippet: error.message,
        },
      ]);
      throw error;
    }
  };

  const handleUserInfoChange = (event) => {
    const { name, value } = event.target;
    setUserInfo((prev) => ({ ...prev, [name]: value }));
  };

  // Generic helper for TMX profile-query
  const runThreatMetrixQuery = async (policy, label = 'ThreatMetrix profile-query', overridePhone) => {
    const { email, firstName, lastName, dob, nationalIdNumber, nationalIdType } = userInfo;
    const { countryCode, telephone } = overridePhone || parsedPhone;
    const requestData = {
      email,
      telephone,
      countryCode,
      firstName,
      lastName,
      dob,
      nationalIdNumber,
      nationalIdType,
      policy,
    };

    const response = await loggedFetch(
      '/api/tmx/profile-query',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      },
      label,
    );

    if (!response.ok) {
      throw new Error(response.statusText);
    }

    return response.json();
  };

  // Velocity: called after User Info submit
  const runThreatMetrixProfileQuery = async (overridePhone) => {
    const data = await runThreatMetrixQuery('hsid_velocity', 'ThreatMetrix profile-query', overridePhone);
    setTmxVelocityResponse(data);
    setTmxVelocityReviewStatus(data.review_status ?? null);
  };

  // Phone Finder: called after OTP verification
  const runThreatMetrixPhoneFinderQuery = async () => {
    const data = await runThreatMetrixQuery('phone_finder_hsid_admin');
    setTmxPhoneFinderResponse(data);
    setTmxPhoneFinderReviewStatus(data.review_status ?? null);
  };

  // FlexID Verification
  const runThreatMetrixFlexIDQuery = async () => {
    const data = await runThreatMetrixQuery('flexid');
    setTmxFlexIdResponse(data);
    setTmxFlexIdReviewStatus(data.review_status ?? null);
  };

  const handleSubmitFlexID = async () => {
    setError('');
    setLoading(true);
    try {
      await runThreatMetrixFlexIDQuery();
      setStepStatus((prev) => ({ ...prev, 'flexid-verification': 'completed' }));
      setSuccessMessage('FlexID verification completed successfully');
      setTimeout(() => {
        setSuccessMessage('');
        setActiveStep(4);
      }, 1500);
    } catch (err) {
      console.error(err);
      setError('Failed to run FlexID verification');
      setStepStatus((prev) => ({ ...prev, 'flexid-verification': 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitUserInfo = async () => {
    setError('');
    if (!userInfo.email || !userInfo.phone || !userInfo.firstName || !userInfo.lastName || !userInfo.dob) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const normalized = userInfo.phone.replace(/[\s\-().]/g, '');
      const phoneMatch = normalized.match(/^(\+\d{1,3}?)(\d{7,12})$/);
      let countryCode = '+1';
      let telephone = normalized.replace(/^\+/, '');

      if (phoneMatch) {
        countryCode = phoneMatch[1];
        telephone = phoneMatch[2];
      } else if (!normalized.startsWith('+')) {
        telephone = normalized.replace(/\D/g, '');
      }

      const parsed = { countryCode, telephone };
      // Update state for subsequent steps (OTP, PBIC, DBIC)
      setParsedPhone(parsed);

      // Use freshly parsed phone for the immediate ThreatMetrix call
      await runThreatMetrixProfileQuery(parsed);

      setStepStatus((prev) => ({ ...prev, 'user-info': 'completed' }));
      setSuccessMessage('User information saved successfully');
      setTimeout(() => {
        setSuccessMessage('');
        setActiveStep(1);
      }, 1500);
    } catch (err) {
      console.error(err);
      setError('Failed to process user information');
      setStepStatus((prev) => ({ ...prev, 'user-info': 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const initiateOTP = async () => {
    setError('');
    setLoading(true);
    try {
      const { countryCode, telephone } = parsedPhone;
      const numericCountryCode = countryCode.replace(/^\+/, '');

      const response = await loggedFetch(
        '/api/aikyam-otp/challenge',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            country_code: numericCountryCode,
            phone_number: telephone,
            method: otpDeliveryMethod,
          }),
        },
        'OTP challenge',
      );

      if (!response.ok) throw new Error(response.statusText);

      const data = await response.json();
      setOtpChallengeId(data.id);

      const deliveryText = otpDeliveryMethod === 'sms' ? 'SMS' : 'Voice Call';
      setSuccessMessage(`OTP sent via ${deliveryText} to ${userInfo.phone}`);
      setTimeout(() => {
        setSuccessMessage('');
        // Stay on the same step; UI will switch to OTP verification view
        setActiveStep(1);
      }, 1500);
    } catch (err) {
      console.error(err);
      setError('Failed to initiate OTP. Please try again.');
      setStepStatus((prev) => ({ ...prev, 'phone-possession': 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitOTP = async () => {
    setError('');
    if (!otp) {
      setError('Please enter the OTP');
      return;
    }
    if (!otpChallengeId) {
      setError('OTP session not found. Please go back and resend the OTP.');
      return;
    }

    setLoading(true);
    try {
      const response = await loggedFetch(
        '/api/aikyam-otp/verify',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: otpChallengeId, code: otp }),
        },
        'OTP verify',
      );

      if (!response.ok) {
        throw new Error('OTP_VERIFY_FAILED');
      }

      setStepStatus((prev) => ({ ...prev, 'phone-possession': 'completed' }));
      setOtpVerified(true);
      setSuccessMessage('OTP verified successfully');
      setTimeout(() => {
        setSuccessMessage('');
        setActiveStep(2);
      }, 1500);

      await runThreatMetrixPhoneFinderQuery();

    } catch (err) {
      console.error(err);
      if (err.message === 'OTP_VERIFY_FAILED') {
        setError('Failed to verify OTP. Please check the code and try again.');
        setStepStatus((prev) => ({ ...prev, 'phone-possession': 'error' }));
      } else {
        setError('Failed to verify user information after OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSkipPhonePossession = () => {
    setError('');
    setStepStatus((prev) => ({ ...prev, 'phone-possession': 'completed' }));
    setSuccessMessage('Skipping phone possession and continuing to PBIC verification');
    setTimeout(() => {
      setSuccessMessage('');
      setActiveStep(2);
    }, 1000);
  };

  const initiateDBICVerification = async () => {
    setError('');
    setLoading(true);
    try {
      const result = await runThreatMetrixQuery('idverse-standalone', 'DBIC initiate (idverse-standalone)');
      handleDBICResponse(result);
    } catch (err) {
      console.error(err);
      setError('Failed to initiate DBIC verification');
      setLoading(false);
    }
  };

  const fetchIDVerseResult = async (authSelectionID) => {
    try {
      const { firstName, lastName, dob } = userInfo;
      const formattedDob = dob ? new Date(dob).toISOString().slice(0, 10).replace(/-/g, '') : '';
      const response = await loggedFetch(
        '/api/tmx/idverse-result',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            firstName,
            lastName,
            dob: formattedDob,
            conditionAttrib3: authSelectionID,
          }),
        },
        'IDVerse result',
      );
      if (response.ok) {
        const result = await response.json();
        setTmxIdverseResponse(result);
        setTmxIdverseReviewStatus(result.review_status ?? null);
        return result;
      }
    } catch (err) {
      console.error('Failed to fetch IDVerse result:', err);
    }
    return null;
  };

  const checkDBICTransactionStatus = (requestId, authSelectionID, isFirstPoll = true) => {
    const fetchFn = isFirstPoll
      ? loggedFetch(`/api/tmx/status/${requestId}`, {}, 'DBIC status')
      : fetch(`/api/tmx/status/${requestId}`);
    fetchFn
      .then((response) => {
        if (!response.ok) throw new Error(response.statusText);
        return response.json();
      })
      .then(async (data) => {
        let requestCompleted = false;
        for (const activity of (data.request_id_activities || [])) {
          if (activity === '_CHALLENGE_PASSED') {
            requestCompleted = true;
            swalAlert?.close();
            setLoading(false);
            setDbicPolling(false);
            const idverseResult = await fetchIDVerseResult(authSelectionID);
            const idverseStatus = idverseResult?.review_status?.toLowerCase();
            if (idverseStatus === 'pass') {
              setDbicVerified(true);
              setStepStatus((prev) => ({ ...prev, 'dbic-verification': 'completed' }));
              setSuccessMessage('Document verification completed successfully — IDVerse: PASS');
              setTimeout(() => {
                setSuccessMessage('');
                setActiveStep(5);
              }, 2000);
            } else if (idverseStatus === 'reject') {
              setError('Document verification failed — IDVerse: REJECT');
              setStepStatus((prev) => ({ ...prev, 'dbic-verification': 'error' }));
            } else {
              setError(`Document verification outcome — IDVerse: ${idverseResult?.review_status ?? 'N/A'}`);
              setStepStatus((prev) => ({ ...prev, 'dbic-verification': 'error' }));
            }
          } else if (activity === '_CHALLENGE_FAILED' || activity === 'Rejected: Non Fraud') {
            requestCompleted = true;
            swalAlert?.close();
            setLoading(false);
            setDbicPolling(false);
            const idverseResult = await fetchIDVerseResult(authSelectionID);
            const idverseStatus = idverseResult?.review_status?.toLowerCase();
            if (idverseStatus === 'reject') {
              setError('Document verification failed — IDVerse: REJECT');
            } else {
              setError(`Document verification failed — IDVerse: ${idverseResult?.review_status ?? 'N/A'}`);
            }
            setStepStatus((prev) => ({ ...prev, 'dbic-verification': 'error' }));
          }
        }
        if (!requestCompleted) {
          setTimeout(() => checkDBICTransactionStatus(requestId, authSelectionID, false), 6000);
        }
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
        setDbicPolling(false);
        setError('Failed to check verification status');
      });
  };

  const handleDBICResponse = (data) => {
    if (data.request_result === 'success' && data.review_status === 'pass') {
      setLoading(false);
      setDbicVerified(true);
      setStepStatus((prev) => ({ ...prev, 'dbic-verification': 'completed' }));
      setSuccessMessage('Document verification completed successfully');
      setTimeout(() => {
        setSuccessMessage('');
        setActiveStep(5);
      }, 2000);
    } else if (
      data.request_result === 'success' &&
      (data.review_status === 'challenge' || data.review_status === 'review')
    ) {
      const authSelectionID = data.auth_selection_id_secure;
      if (!authSelectionID) {
        setLoading(false);
        setError('Authentication Selection ID missing in response.');
        return;
      }

      setDbicAuthSelectionID(authSelectionID);

      const isLocalhost =
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1';
      const baseUrl = isLocalhost
        ? 'https://staging.optum.idkit.co'
        : 'https://verify.healthsafe-id.com';

      QRCode.toDataURL(`${baseUrl}/${authSelectionID}`)
        .then((url) => {
          setLoading(false);
          const alert = Swal.fire({
            html: `<p style="margin-bottom:8px">Scan this QR code with your mobile device to complete document verification.</p><img src="${url}" style="max-width:300px" />`,
            confirmButtonText: 'Done',
            allowOutsideClick: false,
            allowEscapeKey: false,
          });
          setSwalAlert(alert);
          setDbicPolling(true);
          checkDBICTransactionStatus(data.request_id, authSelectionID);
        })
        .catch((err) => {
          console.error(err);
          setLoading(false);
          setError('Failed to generate QR code');
        });
    } else if (
      data.request_result === 'success' &&
      data.review_status === 'reject'
    ) {
      setLoading(false);
      setError('Document verification was rejected.');
      setStepStatus((prev) => ({ ...prev, 'dbic-verification': 'error' }));
    } else {
      setLoading(false);
      setError(`DBIC verification failed: ${data.error_detail || 'Unknown error'}`);
      setStepStatus((prev) => ({ ...prev, 'dbic-verification': 'error' }));
    }
  };

  const checkVerificationStatus = (id, { showDialogOnCompletion = false } = {}) => {
    console.info('Checking verification status', id);
    fetch(`/api/verified-id/request-status/${id}`)
      .then((response) => {
        if (!response.ok) throw new Error(response.statusText);
        return response.json();
      })
      .then((data) => {
        let sweetAlert = null;

        if (verifiedIdPreviousStatus === data.status) {
          setTimeout(() => checkVerificationStatus(id, { showDialogOnCompletion }), 4000);
          return;
        }
        setVerifiedIdPreviousStatus(data.status);

        switch (data.status) {
          case 'verification_request_created':
          case 'issuance_request_created':
            setVerifiedIdInProgress(true);
            setTimeout(() => checkVerificationStatus(id, { showDialogOnCompletion }), 4000);
            break;
          case 'request_completed':
          case 'presentation_verified':
          case 'issuance_successful':
          case 'issuance_error': {
            swalAlert?.close();
            if (!showDialogOnCompletion) {
              sweetAlert = Swal.fire({
                html: data.message,
                confirmButtonText: 'Close',
              });
            }
            setVerifiedIdStatus(data.status);
            setVerifiedIdDetails(data.message);
            setVerifiedIdInProgress(false);

            if (showDialogOnCompletion && data.message) {
              setVerifiedIdDialogTitle('Verified ID Details');
              setVerifiedIdDialogOpen(true);
            }

            const isSuccess =
              data.status === 'presentation_verified' ||
              data.status === 'issuance_successful' ||
              data.status === 'request_completed';

            if (isSuccess) {
              setStepStatus((prev) => ({
                ...prev,
                'verified-id': 'completed',
                'verification-complete': 'completed',
              }));
              setSuccessMessage('Verified ID process completed successfully');
              setTimeout(() => {
                setSuccessMessage('');
                setActiveStep(6);
              }, 2000);
            } else {
              setStepStatus((prev) => ({
                ...prev,
                'verified-id': 'error',
              }));
            }
            break;
          }
          default: {
            swalAlert?.close();
            if (!showDialogOnCompletion) {
              sweetAlert = Swal.fire({
                html: data.message,
                confirmButtonText: 'Cancel',
              });
            }
            setVerifiedIdStatus(data.status);
            setVerifiedIdDetails(data.message);
            setVerifiedIdInProgress(true);
            setTimeout(() => checkVerificationStatus(id, { showDialogOnCompletion }), 4000);
            break;
          }
        }
        setSwalAlert(sweetAlert);
      })
      .catch((error) => {
        console.error(error);
        setVerifiedIdInProgress(false);
        setError('Failed to check Verified ID status');
      });
  };

  const handleIssuanceResponse = (data, isPresentation, showDialogOnCompletion = false) => {
    console.info('Issuance response', data);
    let sweetAlert = null;
    QRCode.toDataURL(data.url)
      .then((url) => {
        checkVerificationStatus(data.requestId, { showDialogOnCompletion });
        if (isPresentation) {
          sweetAlert = Swal.fire({
            html: `<img src="${url}" />`,
            confirmButtonText: 'Cancel',
          });
        } else {
          sweetAlert = Swal.fire({
            html: `<img src="${url}" /><br/>Pin: ${data.pin}`,
            confirmButtonText: 'Cancel',
          });
        }
        setSwalAlert(sweetAlert);
      })
      .catch((err) => {
        console.error(err);
        setVerifiedIdInProgress(false);
        setError('Failed to generate QR code for Verified ID');
      });
  };

  const createIssuanceRequest = () => {
    setError('');
    setLoading(true);
    setVerifiedIdInProgress(true);
    console.info('Creating issuance request');
    loggedFetch(
      '/api/verified-id/issuer/issuance-request',
      {},
      'Verified ID issuance request',
    )
      .then((response) => {
        setLoading(false);
        if (!response.ok) throw new Error(response.statusText);
        return response.json();
      })
      .then((data) => {
        handleIssuanceResponse(data, false);
      })
      .catch((error) => {
        console.error(error);
        setVerifiedIdInProgress(false);
        setError('Failed to create Verified ID issuance request');
      });
  };

  const createPresentationRequest = () => {
    setError('');
    setLoading(true);
    setVerifiedIdInProgress(true);
    console.info('Creating presentation request');
    loggedFetch(
      '/api/verified-id/verifier/presentation-request',
      {},
      'Verified ID presentation request',
    )
      .then((response) => {
        setLoading(false);
        if (!response.ok) throw new Error(response.statusText);
        return response.json();
      })
      .then((data) => {
        handleIssuanceResponse(data, true);
      })
      .catch((error) => {
        console.error(error);
        setVerifiedIdInProgress(false);
        setError('Failed to create Verified ID presentation request');
      });
  };

  const handleVerifyExistingVerifiedIdFromUserInfo = () => {
    setError('');
    setVerifiedIdStatus(null);
    setVerifiedIdDetails(null);
    setVerifiedIdPreviousStatus(null);
    setVerifiedIdDialogTitle('Verified ID Details');
    setLoading(true);
    setVerifiedIdInProgress(true);
    console.info('Creating presentation request from User Info tab');

    loggedFetch(
      '/api/verified-id/verifier/presentation-request',
      {},
      'Verified ID presentation request (User Info)',
    )
      .then((response) => {
        setLoading(false);
        if (!response.ok) throw new Error(response.statusText);
        return response.json();
      })
      .then((data) => {
        console.info('Presentation response (User Info)', data);
        // Reuse issuance handler but open dialog on completion
        handleIssuanceResponse(data, true, true);
      })
      .catch((error) => {
        console.error(error);
        setVerifiedIdInProgress(false);
        setError('Failed to create Verified ID presentation request');
      });
  };

  const handleReset = () => {
    setActiveStep(0);
    setUserInfo({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dob: '',
      nationalIdNumber: '',
      nationalIdType: '',
    });
    setParsedPhone({ countryCode: '+1', telephone: '' });
    setOtpDeliveryMethod('sms');
    setOtp('');
    setOtpVerified(false);
    setOtpChallengeId(null);
    setTmxVelocityResponse(null);
    setTmxVelocityReviewStatus(null);
    setTmxPhoneFinderResponse(null);
    setTmxPhoneFinderReviewStatus(null);
    setTmxFlexIdResponse(null);
    setTmxFlexIdReviewStatus(null);
    setTmxModalOpen(false);
    setTmxModalProfile('velocity');
    setDbicVerified(false);
    setDbicAuthSelectionID(null);
    setTmxIdverseResponse(null);
    setTmxIdverseReviewStatus(null);
    setError('');
    setSuccessMessage('');
    setStepStatus({
      'user-info': 'pending',
      'phone-possession': 'pending',
      'pbic-verification': 'pending',
      'flexid-verification': 'pending',
      'dbic-verification': 'pending',
      'verified-id': 'pending',
      'verification-complete': 'pending',
    });
    setVerifiedIdStatus(null);
    setVerifiedIdDetails(null);
    setVerifiedIdInProgress(false);
    setVerifiedIdPreviousStatus(null);
    setVerifiedIdDialogOpen(false);
    setResponseModalOpen(false);
    setSelectedResponse(null);
    setResponseViewMode('json');
  };

  const getStepStatus = (index) => {
    const stepId = STEPS[index].id;
    if (stepStatus[stepId] === 'completed') return 'completed';
    if (stepStatus[stepId] === 'error') return 'error';
    return activeStep >= index ? 'active' : 'pending';
  };

  return (
    <div className={classes.root}>
      <Backdrop
        sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container className={classes.container}>
        <Box display="flex" justifyContent="flex-end" mb={1}>
          <Link
            component="button"
            variant="body2"
            onClick={() => setPanelOpen(true)}
            underline="hover"
          >
            View Request Panel
          </Link>
        </Box>
        {activeStep > 0 && userInfo.email && userInfo.phone && (
          <Box className={classes.userInfoBanner}>
            <Typography variant="body2" color="textSecondary">
              <strong>Contact Information:</strong>
            </Typography>
            <Typography variant="body1">
              📧 {userInfo.email} | 📱 {userInfo.phone}
            </Typography>

            {Object.values(tmxResults).some((r) => r.status) && (
              <Box
                style={{
                  marginTop: '0.5rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.35rem',
                }}
              >
                {Object.entries(tmxResults).map(([key, { status }]) =>
                  status ? (
                    <Box
                      key={key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Chip
                        label={`${TMX_PROFILES[key].chipLabel}: ${status.toUpperCase()}`}
                        size="small"
                        style={getStatusChipStyle(status)}
                      />
                      <Link
                        component="button"
                        variant="caption"
                        onClick={() => {
                          setTmxModalProfile(key);
                          setTmxModalOpen(true);
                        }}
                        underline="hover"
                      >
                        View {TMX_PROFILES[key].chipLabel} response
                      </Link>
                    </Box>
                  ) : null
                )}
              </Box>
            )}
          </Box>
        )}
        <Card className={classes.card}>
          <Box className={classes.stepperContainer}>
            <Stepper activeStep={activeStep}>
              {STEPS.map((step, index) => (
                <Step key={step.id} completed={getStepStatus(index) === 'completed'}>
                  <StepLabel error={getStepStatus(index) === 'error'}>
                    {step.label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <CardContent className={classes.contentCard}>
            {successMessage && (
              <Alert severity="success" style={{ marginBottom: '1rem' }}>
                {successMessage}
              </Alert>
            )}

            {error && (
              <Alert severity="error" style={{ marginBottom: '1rem' }}>
                {error}
              </Alert>
            )}

            <div className={classes.stepContent}>
              {activeStep === 0 && (
                <UserInfoForm
                  classes={classes}
                  userInfo={userInfo}
                  stepStatus={stepStatus}
                  onChange={handleUserInfoChange}
                  onSubmit={handleSubmitUserInfo}
                  onVerifyExistingVerifiedId={handleVerifyExistingVerifiedIdFromUserInfo}
                  verifiedIdInProgress={verifiedIdInProgress}
                />
              )}

              {activeStep === 1 && (
                otpChallengeId ? (
                  <OtpVerificationStep
                    classes={classes}
                    otp={otp}
                    setOtp={setOtp}
                    otpDeliveryMethod={otpDeliveryMethod}
                    phone={userInfo.phone}
                    onBack={() => {
                      setOtp('');
                      setOtpChallengeId(null);
                    }}
                    onVerify={handleSubmitOTP}
                    onSkip={handleSkipPhonePossession}
                  />
                ) : (
                  <OtpInitiationStep
                    classes={classes}
                    otpDeliveryMethod={otpDeliveryMethod}
                    setOtpDeliveryMethod={setOtpDeliveryMethod}
                    phone={userInfo.phone}
                    stepStatus={stepStatus}
                    onBack={() => setActiveStep(0)}
                    onSend={initiateOTP}
                  />
                )
              )}

              {activeStep === 2 && (
                <Box className={classes.stepContent}>
                  <Typography variant="h6" style={{ marginBottom: '1rem' }}>
                    PBIC Verification
                  </Typography>
                  {tmxPhoneFinderReviewStatus ? (
                    <Box
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.75rem',
                      }}
                    >
                      <Chip
                        label={`Phone Finder: ${tmxPhoneFinderReviewStatus.toUpperCase()}`}
                        size="small"
                        style={getStatusChipStyle(tmxPhoneFinderReviewStatus)}
                      />
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => {
                          setTmxModalProfile('phoneFinder');
                          setTmxModalOpen(true);
                        }}
                        underline="hover"
                      >
                        View Phone Finder response
                      </Link>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Phone Finder result is not available yet.
                    </Typography>
                  )}
                  <Box className={classes.buttonContainer}>
                    <Button
                      variant="outlined"
                      onClick={() => setActiveStep(1)}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={async () => {
                        setStepStatus((prev) => ({
                          ...prev,
                          'pbic-verification': 'completed',
                        }));
                        await handleSubmitFlexID();
                      }}
                      disabled={loading}
                    >
                      Run FlexID Verification
                    </Button>
                  </Box>
                </Box>
              )}

              {activeStep === 3 && (
                <Box className={classes.stepContent}>
                  <Typography variant="h6" style={{ marginBottom: '1rem' }}>
                    FlexID Verification
                  </Typography>
                  {tmxFlexIdReviewStatus ? (
                    <Box
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.75rem',
                      }}
                    >
                      <Chip
                        label={`FlexID: ${tmxFlexIdReviewStatus.toUpperCase()}`}
                        size="small"
                        style={getStatusChipStyle(tmxFlexIdReviewStatus)}
                      />
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => {
                          setTmxModalProfile('flexid');
                          setTmxModalOpen(true);
                        }}
                        underline="hover"
                      >
                        View FlexID response
                      </Link>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Run FlexID verification to continue.
                    </Typography>
                  )}
                  <Box className={classes.buttonContainer}>
                    <Button variant="outlined" onClick={() => setActiveStep(2)}>
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSubmitFlexID}
                      disabled={loading}
                    >
                      Run FlexID Verification
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setActiveStep(4)}
                      disabled={!tmxFlexIdReviewStatus}
                    >
                      Continue to DBIC
                    </Button>
                  </Box>
                </Box>
              )}

              {activeStep === 4 && (
                dbicPolling ? (
                  <Box className={classes.pollingCard}>
                    <Box className={classes.scannerBox}>
                      <Box className={classes.scanLine} />
                      <CheckCircleOutlineIcon className={classes.scannerIcon} />
                    </Box>
                    <Typography variant="h6" style={{ fontWeight: 600 }}>
                      Verifying your document
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', maxWidth: 320 }}>
                      Please complete the verification on your mobile device. This page will update automatically.
                    </Typography>
                    <Box className={classes.dotContainer}>
                      <Box className={classes.dot} style={{ animationDelay: '0s' }} />
                      <Box className={classes.dot} style={{ animationDelay: '0.2s' }} />
                      <Box className={classes.dot} style={{ animationDelay: '0.4s' }} />
                      <Box className={classes.dot} style={{ animationDelay: '0.6s' }} />
                    </Box>
                  </Box>
                ) : (
                  <Box className={classes.dbicContainer}>
                    <Typography variant="h6" style={{ marginBottom: '1rem' }}>
                      Document-Based Identity Confirmation (DBIC)
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: '1rem' }}>
                      Click the button below to start document-based identity verification.
                    </Typography>
                    <Box className={classes.buttonContainer}>
                      <Button variant="outlined" onClick={() => setActiveStep(3)}>
                        Back
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={initiateDBICVerification}
                        disabled={loading}
                      >
                        Start Verification
                      </Button>
                    </Box>
                  </Box>
                )
              )}

              {activeStep === 5 && (
                <Box className={classes.stepContent}>
                  <Typography variant="h6" style={{ marginBottom: '1rem', textAlign: 'center' }}>
                    Verified ID
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: '1rem', textAlign: 'center' }}>
                    Issue a new Verified ID credential or verify an existing one.
                  </Typography>

                  <Box className={classes.buttonContainer} style={{ justifyContent: 'center', marginBottom: '1.5rem' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={createIssuanceRequest}
                      disabled={loading || verifiedIdInProgress}
                    >
                      Issue Verified ID
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={createPresentationRequest}
                      disabled={loading || verifiedIdInProgress}
                    >
                      Verify Existing Verified ID
                    </Button>
                  </Box>

                  {verifiedIdStatus && (
                    <Box
                      style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        borderRadius: 8,
                        border: '1px solid #e0e0e0',
                        backgroundColor: '#fafafa',
                      }}
                    >
                      <Typography variant="subtitle2" gutterBottom>
                        Verified ID Status: {verifiedIdStatus}
                      </Typography>
                      {verifiedIdDetails && (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          component="div"
                          style={{ marginTop: '0.5rem', wordBreak: 'break-word' }}
                          dangerouslySetInnerHTML={{ __html: verifiedIdDetails }}
                        />
                      )}
                    </Box>
                  )}

                  {!verifiedIdStatus && verifiedIdInProgress && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{ marginTop: '1rem', textAlign: 'center' }}
                    >
                      Waiting for Verified ID to be completed on the user device...
                    </Typography>
                  )}

                  <Box className={classes.buttonContainer}>
                    <Button
                      variant="outlined"
                      onClick={() => setActiveStep(4)}
                      disabled={loading}
                    >
                      Back
                    </Button>
                  </Box>
                </Box>
              )}

              {activeStep === 6 && (
                <Box style={{ textAlign: 'center' }}>
                  <CheckCircleOutlineIcon style={{ fontSize: '4rem', color: '#4caf50', marginBottom: '1rem' }} />
                  <Typography variant="h5" style={{ marginBottom: '1rem' }}>
                    Verification Complete!
                  </Typography>
                  <Typography variant="body1" color="textSecondary" style={{ marginBottom: '1rem' }}>
                    {userInfo.firstName} {userInfo.lastName}, your identity has been verified successfully.
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: '2rem' }}>
                    {otpVerified ? '✓ OTP Verified' : ''}
                    {otpVerified && dbicVerified ? ' | ' : ''}
                    {dbicVerified ? '✓ DBIC Verified' : ''}
                    {stepStatus['verified-id'] === 'completed' ? ' | ✓ Verified ID Issued/Verified' : ''}
                  </Typography>
                  <Box className={classes.buttonContainer}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleReset}
                    >
                      Start Over
                    </Button>
                  </Box>
                </Box>
              )}
            </div>
          </CardContent>
        </Card>
      </Container>

      <Dialog
        open={tmxModalOpen}
        onClose={() => setTmxModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {(() => {
          const { status: currentStatus, response: currentResponse } = tmxResults[tmxModalProfile] || {};
          const { profileLabel } = TMX_PROFILES[tmxModalProfile] || {};

          return (
            <>
              <DialogTitle>
                ThreatMetrix Profile-Query Response (
                {profileLabel}
                )
                {currentStatus && (
                  <Chip
                    label={currentStatus.toUpperCase()}
                    size="small"
                    style={{
                      marginLeft: '0.75rem',
                      ...getStatusChipStyle(currentStatus),
                    }}
                  />
                )}
              </DialogTitle>
              <DialogContent dividers>
                <Box
                  component="pre"
                  style={{
                    margin: 0,
                    fontSize: '0.8rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace',
                  }}
                >
                  {currentResponse
                    ? JSON.stringify(currentResponse, null, 2)
                    : 'No response available.'}
                </Box>
              </DialogContent>
            </>
          );
        })()}
        <DialogActions>
          <Button onClick={() => setTmxModalOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={verifiedIdDialogOpen}
        onClose={() => setVerifiedIdDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{verifiedIdDialogTitle}</DialogTitle>
        <DialogContent dividers>
          {verifiedIdDetails ? (
            <Box
              component="div"
              style={{
                margin: 0,
                fontSize: '0.9rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
              dangerouslySetInnerHTML={{ __html: verifiedIdDetails }}
            />
          ) : (
            <Typography variant="body2" color="textSecondary">
              No Verified ID details available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifiedIdDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={responseModalOpen}
        onClose={() => {
          setResponseModalOpen(false);
          setSelectedResponse(null);
          setResponseViewMode('json');
        }}
        maxWidth="md"
        fullWidth
      >
        {selectedResponse && (
          <>
            <DialogTitle>
              Response Details - {selectedResponse.label}
              {selectedResponse.profile && (
                <Chip
                  label={selectedResponse.profile}
                  size="small"
                  style={{ marginLeft: '0.75rem', backgroundColor: '#fff3e0', color: '#e65100' }}
                />
              )}
            </DialogTitle>
            <Box display="flex" gap={1} px={2} py={1}>
              <Button
                size="small"
                variant={responseViewMode === 'json' ? 'contained' : 'outlined'}
                onClick={() => setResponseViewMode('json')}
              >
                JSON
              </Button>
              <Button
                size="small"
                variant={responseViewMode === 'raw' ? 'contained' : 'outlined'}
                onClick={() => setResponseViewMode('raw')}
              >
                Raw
              </Button>
            </Box>
            <DialogContent dividers>
              {responseViewMode === 'json' ? (
                (() => {
                  try {
                    const parsed = JSON.parse(selectedResponse.responseBody);
                    return (
                      <Box
                        component="pre"
                        style={{
                          margin: 0,
                          fontSize: '0.8rem',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontFamily: 'monospace',
                          backgroundColor: '#f5f5f5',
                          padding: '1rem',
                          borderRadius: '4px',
                          maxHeight: '500px',
                          overflow: 'auto',
                        }}
                      >
                        {JSON.stringify(parsed, null, 2)}
                      </Box>
                    );
                  } catch (e) {
                    return (
                      <Alert severity="warning">
                        <Typography variant="body2">
                          Unable to parse as JSON. Showing raw response instead.
                        </Typography>
                        <Box
                          component="pre"
                          style={{
                            margin: '1rem 0 0 0',
                            fontSize: '0.8rem',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontFamily: 'monospace',
                            backgroundColor: '#f5f5f5',
                            padding: '1rem',
                            borderRadius: '4px',
                            maxHeight: '400px',
                            overflow: 'auto',
                          }}
                        >
                          {selectedResponse.responseBody}
                        </Box>
                      </Alert>
                    );
                  }
                })()
              ) : (
                <Box
                  component="pre"
                  style={{
                    margin: 0,
                    fontSize: '0.8rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace',
                    backgroundColor: '#f5f5f5',
                    padding: '1rem',
                    borderRadius: '4px',
                    maxHeight: '500px',
                    overflow: 'auto',
                  }}
                >
                  {selectedResponse.responseBody}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(selectedResponse.responseBody);
                }}
                variant="outlined"
              >
                Copy
              </Button>
              <Button onClick={() => setResponseModalOpen(false)} variant="contained">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {panelOpen && (
        <Box
          position="fixed"
          bottom={16}
          right={16}
          width={460}
          zIndex={(theme) => theme.zIndex.drawer + 2}
        >
          <Card elevation={8} style={{ borderRadius: 12, overflow: 'hidden' }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              px={2}
              py={1.25}
              style={{
                background: 'linear-gradient(135deg, #1a237e 0%, #1565c0 100%)',
                color: '#fff',
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle2" style={{ fontWeight: 700, letterSpacing: 0.5 }}>
                  Network Log
                </Typography>
                <Chip
                  label={`${networkLogs.length}`}
                  size="small"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    height: 20,
                  }}
                />
              </Box>
              <Button
                size="small"
                style={{ color: '#fff', minWidth: 'auto', fontWeight: 600 }}
                onClick={() => setPanelOpen(false)}
              >
                ✕
              </Button>
            </Box>

            <CardContent style={{ maxHeight: 400, overflow: 'auto', padding: '12px 16px', backgroundColor: '#f8f9fb' }}>
              {threatMetrixScriptUrl && (
                <Box
                  mb={2}
                  p={1.5}
                  style={{
                    backgroundColor: '#ede7f6',
                    borderRadius: 8,
                    borderLeft: '4px solid #7c4dff',
                  }}
                >
                  <Typography variant="caption" style={{ color: '#4a148c', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.65rem' }}>
                    ThreatMetrix Script
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ wordBreak: 'break-all', fontFamily: '"Fira Code", "Cascadia Code", monospace', fontSize: '0.75rem', marginTop: 4, color: '#311b92' }}
                  >
                    {threatMetrixScriptUrl}
                  </Typography>
                </Box>
              )}

              {networkLogs.length === 0 && (
                <Box style={{ textAlign: 'center', padding: '2rem 0' }}>
                  <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
                    No requests captured yet.
                  </Typography>
                </Box>
              )}

              {networkLogs
                .slice()
                .reverse()
                .map((log) => (
                  <Box
                    key={log.id}
                    mb={1.5}
                    p={1.5}
                    style={{
                      borderRadius: 8,
                      backgroundColor: '#fff',
                      border: `1px solid ${log.ok ? '#e0e0e0' : '#ffcdd2'}`,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                      transition: 'box-shadow 0.2s',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      setSelectedResponse(log);
                      setResponseModalOpen(true);
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <Chip
                          label={log.method}
                          size="small"
                          style={{
                            backgroundColor: log.ok ? '#e3f2fd' : '#ffebee',
                            color: log.ok ? '#0d47a1' : '#b71c1c',
                            fontWeight: 700,
                            fontSize: '0.65rem',
                            height: 20,
                            borderRadius: 4,
                          }}
                        />
                        <Chip
                          label={log.status}
                          size="small"
                          style={{
                            backgroundColor: log.ok ? '#e8f5e9' : '#ffebee',
                            color: log.ok ? '#1b5e20' : '#b71c1c',
                            fontWeight: 600,
                            fontSize: '0.65rem',
                            height: 20,
                            borderRadius: 4,
                          }}
                        />
                      </Box>
                      <Typography variant="caption" style={{ color: '#9e9e9e', fontSize: '0.65rem' }}>
                        {log.durationMs} ms
                      </Typography>
                    </Box>

                    {log.profile && (
                      <Box
                        display="inline-flex"
                        alignItems="center"
                        mb={0.5}
                        px={1}
                        py={0.25}
                        style={{
                          backgroundColor: '#fff3e0',
                          borderRadius: 6,
                          border: '1px solid #ffe0b2',
                        }}
                      >
                        <Typography
                          variant="caption"
                          style={{
                            fontWeight: 800,
                            color: '#e65100',
                            fontSize: '0.72rem',
                            letterSpacing: 0.3,
                          }}
                        >
                          ⚡ {log.profile}
                        </Typography>
                      </Box>
                    )}

                    {log.label && !log.profile && (
                      <Typography variant="caption" display="block" style={{ color: '#616161', marginBottom: 2 }}>
                        {log.label}
                      </Typography>
                    )}

                    <Typography
                      variant="body2"
                      style={{
                        wordBreak: 'break-all',
                        fontFamily: '"Fira Code", "Cascadia Code", monospace',
                        fontSize: '0.72rem',
                        color: '#37474f',
                        lineHeight: 1.5,
                      }}
                    >
                      {log.url}
                    </Typography>

                    {log.error && (
                      <Typography
                        variant="caption"
                        style={{
                          display: 'block',
                          marginTop: 4,
                          color: '#c62828',
                          backgroundColor: '#ffebee',
                          padding: '2px 6px',
                          borderRadius: 4,
                          fontSize: '0.65rem',
                        }}
                      >
                        {log.error}
                      </Typography>
                    )}

                    {log.responseBody && (
                      <Link
                        component="button"
                        variant="caption"
                        style={{
                          display: 'block',
                          marginTop: '0.5rem',
                          textAlign: 'left',
                          color: '#1976d2',
                          fontSize: '0.75rem',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedResponse(log);
                          setResponseModalOpen(true);
                        }}
                        underline="hover"
                      >
                        📄 View Response
                      </Link>
                    )}
                  </Box>
                ))}
            </CardContent>
          </Card>
        </Box>
      )}
    </div>
  );
};

export default OTPProgressPage;
